<template>
    <div ref="showDialog" class="polygon-upload">
        <div class="upload-polygon-button">
            <md-button title="Upload Area" class="md-icon-button" @click="showDialog = !showDialog"><md-icon>upload</md-icon></md-button>
        </div>
        <md-dialog :md-active.sync="showDialog" @md-closed="dialogClosed" class="dialog">
            <md-dialog-title>Upload Areas</md-dialog-title>
            <md-dialog-content class="dialog-content">
                <md-content class="content">
                    <div class="upload-container">
                        <p>File formats supported for upload: Shape file zip, KML file, KMZ file, GeoJSON file.</p>
                        <md-field v-if="isUpload" :class="isUserFilenameError ? 'md-invalid' : ''">
                            <md-icon>text_snippet</md-icon>
                            <label>Enter a filename</label>
                            <md-input v-model="fileName" required @input="checkFileName()"></md-input>
                            <span class="md-error">{{filenameErrorMessage}}</span>
                        </md-field>
                        <md-field :class="isUserError ? 'md-invalid' : ''">
                            <label>Upload file</label>
                            <md-file :accept="acceptedTypes" @md-change="checkFiles($event)"/>
                            <span class="md-error">{{fileErrorMessage}}</span>
                        </md-field>
                        <md-checkbox v-model="isUpload" @change="fileName = ''; isUserFilenameError = true">
                            <span>Upload area file to long term storage (recommended for large or complex geometries)</span>
                        </md-checkbox>
                    </div>
                </md-content>
            </md-dialog-content>
            <md-dialog-actions>
                <md-button class="md-raised md-secondary" @click="cancelUpload">
                    Cancel
                </md-button>
                <md-button v-if="!showProgressBar" class="md-raised md-primary" @click="checkFileName(); startUpload(); ebxLogAnalytics(GA_EVENTS.DIALOG_UPLOAD_POLYGONS);" :disabled="!isClean || isUserError || (isUpload && isUserFilenameError)">
                    {{ isUpload ? 'Start Upload' : 'Add to map' }}
                </md-button>
                <div v-else-if="showProgressBar" class="progress-container">
                    <p>{{loadingStatus}}</p>
                    <md-progress-bar class="md-accent" md-mode="indeterminate"></md-progress-bar>
                </div>
            </md-dialog-actions>
        </md-dialog>
        <md-snackbar
            :md-position="'center'"
            :md-active.sync="snackbarActive"
            :md-duration="10000"
            md-persistent
            >
            <span>{{ snackbarMessage }}</span>
        </md-snackbar>
    </div>
</template>

<script>
/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
*/
import { AreaService } from "@/services/area.service";
import { AuthService } from "@/services/auth.service";
import { storage } from "@/firebase.js";
import { organisationsCollection } from '../../firebase';
import firebase from "firebase/app"
// const { v4: uuidv4 } = require('uuid');
import {projectId} from "@/firebase.js";
import {getGeoJson, getPolygons, getBbox} from "@/scripts/ShapefileHandlers.js";
// import { functions } from "@/firebaseFunctions.js";

const EXTRA_EE_PROJECT_MAPPINGS = {
    'ebx-trial-dev': 'ee-alpha',
    'ebx-trial': 'ee-alpha',
    'ee-stem': 'ee-unep',
    'ebx-mooc': 'ee-uoe',
    'eb-edina': 'ee-uoe',
}

export default {
    props: {
        google: {
            type: Object,
            required: true
        },
        map: {
            type: Object,
            required: true
        },
        selectedArea: {
            type: Object,
            required: true
        },
        mapMeasureMode: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            showDialog: false,
            isClean: false,
            isUserError: false,
            isUserFilenameError: true, // default to true
            errorMessage: "There is an error",
            isFileUploaded: false,
            filePolygons: [],
            fileErrorMessage: "There is an error",
            fileName: "",
            filenameErrorMessage: "Invalid filename",
            showProgressBar: false,
            allowedExtensions:['.json','.geojson','.kml','.kmz','.zip'],
            acceptedTypes: '',
            filesToUpload: [],
            user: null,
            bucket: null,
            loadingStatus: 'Loading',
            operationPoller: null,
            isUpload: false,
            snackbarActive: false,
            snackbarMessage: "",
        }
    },
    mounted() {
        const showDialogView = this.$refs.showDialog;
        this.map.controls[this.google.maps.ControlPosition.RIGHT_TOP].push(showDialogView)
        this.acceptedTypes = this.allowedExtensions.join(',')

        this.subscription = AuthService.loggedUser$.subscribe((user) => {
            this.user = user;
        });

        this.bucket = storage.refFromURL(`gs://unscanned-earthengine-upload-${projectId}/`);

        this.subscription = AreaService.newCoords$.subscribe(function(params) {
            if (params.area && params.area.features.length > 0) {
                let features = params.area.features;
                let latList = [];
                let lngList = [];

                // loop over features to get area name and polygon
                features.forEach(function(feature) {
                    let coords = []
                    let areaName = feature.properties.areaName;
                    let featureCoordSets = feature.geometry.coordinates;
                    featureCoordSets.forEach(coordSet => {
                        coordSet.forEach(coordPair => {
                            coords.push({
                                lat: parseFloat(coordPair[0]),
                                lng: parseFloat(coordPair[1])
                            })
                            latList.push(parseFloat(coordPair[0]))
                            lngList.push(parseFloat(coordPair[1]))
                        })
                    })

                    this.renderAreas(coords, areaName)
                }.bind(this));

                // this is all to get the right coordinate pairs so we can pan the map on the area we loaded


                var maxLat = Math.max.apply(null, latList);
                var maxLng = Math.max.apply(null, lngList);
                var minLat = Math.min.apply(null, latList);
                var minLng = Math.min.apply(null, lngList);

                let bounds = new this.google.maps.LatLngBounds(
                    new this.google.maps.LatLng(minLat, minLng),
                    new this.google.maps.LatLng(maxLat, maxLng)
                    )

                // sets map type defined in featured_worfklows.js - see map types here (https://developers.google.com/maps/documentation/javascript/maptypes)
                if(params.mapType){
                    this.map.setMapTypeId(params.mapType);
                } else {
                    this.map.setMapTypeId('roadmap')
                }

                this.map.fitBounds(bounds,0);
            }
            // push an event to say we've loaded up the area on the map
            AreaService.publishFeatureEvent(params);
        }.bind(this));
    },
    beforeDestroy() {
        this.subscription.unsubscribe();
        let index = this.map.controls[this.google.maps.ControlPosition.RIGHT_TOP].indexOf(this.$refs.showDialog);
        if(index > -1) {
            this.map.controls[this.google.maps.ControlPosition.RIGHT_TOP].removeAt(index);
        }
    },
    methods: {
        // function used to create polygons for specific area names in example workflows
        // area colours fixed in trial-291 (MR = https://gitlab.com/earth-blox-dev-team/earthbloxnextgen/-/merge_requests/311)
        renderAreas(coords, areaName) {
            let selectedArea = this.selectedArea;
            let customAreas = AreaService.getCustomAreas();

            // only add area if we don't already have that name
            if (!customAreas.includes(areaName)) {
                this.$emit('add-area', areaName);
            }

            selectedArea = {
                name: areaName,
                colour: AreaService.getColour(areaName)
            }

            const newPolygon = new this.google.maps.Polygon({
                paths: coords,
                fillOpacity: 0,
                strokeWeight: 4,
                strokeColor: selectedArea.colour
            })

            newPolygon.setMap(this.map);

            AreaService.addUserDrawnShape(selectedArea.name, newPolygon);

            this.isClean = false;
            this.isUserError = false;

            this.setListeners(newPolygon)
        },
        cancelUpload() {
            this.showDialog = false;
            this.cleanUpAfterUpload();
        },
        cleanUpAfterUpload() {
            this.dialogClosed();
        },
        dialogClosed(){
            this.isClean = false;
            this.isUserError = false;
            this.isUserFilenameError = true;
            this.isFileUploaded = false;
            this.loadingStatus = 'Loading';
            this.showProgressBar = false;
            this.filePolygons = [];
            this.filesToUpload = [];
            this.fileName = "";
            this.isUpload = false;
        },
        async createOperationDoc(filesToUpload, uploadJob, storagePath) {
            // TODO dirty fix, should be created on the back end and added to assetid
            let ee_project = projectId
            if(projectId in EXTRA_EE_PROJECT_MAPPINGS) {
                ee_project = EXTRA_EE_PROJECT_MAPPINGS[projectId]
            }

            let docId = `${this.user.uid}_${uploadJob}`

            let assetID = `projects/${ee_project}/assets/${this.user.orgId}/${this.user.uid}/${uploadJob}`

            // check if an operation doc for this upload already exists
            // if it does then raise error as filename is not unique
            let docRef = this.orgRef.collection("operations").doc(docId);
            let docSnapshot = await docRef.get();
            if (docSnapshot.exists) {
                console.warn('Operation document already exists.');
                this.isClean = false;
                this.isUserFilenameError = true;
                this.filenameErrorMessage = 'An upload with this filename already exists.';
                this.showProgressBar = false;
                return;
            }

            // TODO replace with psuedo state enum
            let currentState = "UPLOADING"

            docRef.set({
                files: filesToUpload.map((file) => file.name), // array of file names (if we decide to allow multiple files)
                filename: filesToUpload[0].name, // for single file
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                storagePath: storagePath,
                state: currentState,
                warnings: [],
                submittedBy: this.user.uid,
                mapURL: null,
                assetID: assetID,
                uploadJob: uploadJob,
                task_type: "INGEST_TABLE"
            })

            this.loadingStatus = currentState

            return docRef
        },
        /**
         * Given a file, check if the user wants the file to be uploadded
         * to long term storage, or directly to the map. Uploads file or adds to map
         * using geojson
         */
        startUpload() {
            if(this.isUpload) {
                this.uploadFiles()
            } else {
                this.parseShapeFileLocally()
            }
        },
        uploadFiles() {
            if (this.isClean && !this.isUserError && !this.isUserFilenameError) {
                this.showProgressBar = true;

                // slugify the filename
                let uploadJob = this.fileName.replace(/[^\-_a-zA-Z0-9]/g, '-').toLowerCase();
                let storagePath= `/${this.user.orgId}/${this.user.uid}/${uploadJob}`

                this.createOperationDoc(this.filesToUpload, uploadJob, storagePath).then(operationDocRef => {
                    // this loops over each file and uploads them to storage
                    // resolves if all files uploaded
                    this.uploadToStorageBucket(this.filesToUpload, uploadJob, storagePath).then(() => {
                            // update operation doc, and create status "scanning"
                            let currentState = "SCANNING";
                            operationDocRef.update({
                                state: currentState,
                                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
                            })

                            this.loadingStatus = currentState
                            // now that files are uploaded we should listen to backend changes
                            // this.createOperationDocSnapshotListener(operationDocRef)
                            console.log("closing dialog")
                            this.showDialog = false;
                            this.cleanUpAfterUpload();
                            this.snackbarMessage = "Your file is being processed. Monitor progress in the tasks tab."
                            this.snackbarActive = true;
                        })
                        .catch((file) => {
                            console.error("File failed to upload", file)
                            // update operation doc and create status "ERROR"
                            // give error information
                            let currentState = "ERROR";
                            operationDocRef.update({
                                state: currentState,
                                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
                            })

                            this.loadingStatus = currentState
                            this.showDialog = false;
                            this.cleanUpAfterUpload();
                            this.snackbarMessage = "There was an error uploading your file. Please try again."
                            this.snackbarActive = true;           
                        })
                    });
            }
        },
        /**
         * Parses a shapefile locally and adds to area service and map via
         * polygons
         */
        parseShapeFileLocally() {
            if (this.isClean && !this.isUserError) {
                this.showProgressBar = true;

                this.loadingStatus = "Parsing"

                getGeoJson(this.filesToUpload).then(geojson => {
                    if(!geojson){
                        throw Error("geojson undefined")
                    }
                    let bbox = getBbox(geojson);
                    let polygons = getPolygons(geojson);

                    if(polygons.length > 0) {
                        this.loadingStatus = "Adding to map"
                        this.addPolygonsToMap(polygons, bbox);
                        this.snackbarMessage = ""
                    } else {
                        this.snackbarMessage = "No polygons found in file";
                        this.snackbarActive = true;
                    }
                }).catch(error => {
                    console.error("Error parsing shapefile", error)
                    this.loadingStatus = "Error"
                    // show snackbar
                    this.snackbarMessage = "There was an error parsing your file. Please try uploading your file to long term storage.";
                    this.snackbarActive = true;
                }).finally(() => {
                    this.showDialog = false;
                    this.cleanUpAfterUpload();
                })
            }
        },
        /**
         * Given an array of polygons, and their bounding box, add them to the map
         * and pan to bbox
         * @param {Array} polygons
         */
        addPolygonsToMap(polygons, bbox) {

            polygons.forEach((polygon) => {
                let coords = []

                polygon.forEach(coordPair => {
                    let lat = parseFloat(coordPair[0])
                    let lng = parseFloat(coordPair[1])

                    coords.push({
                        lat: lat,
                        lng: lng
                    })
                })

                this.createPolygon(coords)
            })

            let bounds = new this.google.maps.LatLngBounds(
                new this.google.maps.LatLng(bbox[0], bbox[1]),
                new this.google.maps.LatLng(bbox[2], bbox[3])
            );

            this.map.fitBounds(bounds, 0);
        },
        createPolygon(coords) {
            let customAreas = AreaService.getCustomAreas();


            if(customAreas.length === 0) {
                this.$emit('add-area');
                // set selected area
                this.selectedArea.name = AreaService.getCustomAreas()[0];
                this.selectedArea.colour = AreaService.getColour(this.selectedArea.name);
            }

            let newPolygon = new this.google.maps.Polygon({
                paths: coords,
                fillOpacity: 0,
                strokeWeight: 4,
                strokeColor: this.selectedArea.colour
            });

            newPolygon.setMap(this.map);
            AreaService.addUserDrawnShape(this.selectedArea.name, newPolygon);

            this.setListeners(newPolygon)
        },
        setListeners(polygon) {
            this.$emit('set-overlay-listeners', polygon, 'polygon')
            this.$emit('set-polygon-listeners', polygon)
        },
        checkFileName() {
            if(this.fileName == '') {
                console.error('Error -> no filename entered');
                this.isUserFilenameError = true;
                this.filenameErrorMessage = 'Please enter a filename';
            } else {
                this.isUserFilenameError = false;
            }
        },
        checkFiles(files) {
            // verify we are allowing file type
            if(files.length > 1) {
                console.error('Error -> too many files selected');
                this.isUserError = true;
                this.errorMessage = 'Please only select one file';
                return;
            }

            files.forEach((file) => {
                let extension = this.getFileExtension(file.name);
                if (!this.allowedExtensions.includes(extension)) {
                    console.info('Extension check fails')
                    this.isClean = false;
                    this.isUserError = true;
                    this.fileErrorMessage = "Please upload a supported file type"
                }
                else if (file.name.indexOf(' ') >= 0) {
                    console.error('Error -> whitespace in filename:', file.name);
                    this.isClean = false;
                    this.isUserError = true;
                    this.fileErrorMessage = "Whitespace in filenames is not supported."
                } else {
                    console.info('Extension check passes', file)
                    // this.filesToUpload.push(file)
                    // set filesToUpload as an array with a single file, this means in the future we can support multiple files
                    this.filesToUpload = [file]
                    this.isClean = true;
                    this.isUserError = false;
                }
            });
        },
        uploadToStorageBucket(fileList, uploadJob, storagePath) {
            // function that uploads file list to storage bucket

            return new Promise((resolve, reject) => {
                let totalFiles = fileList.length;
                let nUploadedFiles = 0

                // https://firebase.google.com/docs/storage/web/upload-files#full_example
                fileList.forEach((file) => {
                    this.bucket.child(`${storagePath}/${file.name}`).put(file).on('state_changed',
                        (snapshot) =>{
                            // task progress
                            var progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            console.info("file is " + progress + "% done")

                        },
                        (error) => {
                            // full error list here
                            // https://firebase.google.com/docs/storage/web/handle-errors
                            console.error("Error in uploading file, more details below:", file)
                            console.error("Error code", error.code)
                            console.error(error)
                            reject(file)
                        },
                        () => {
                            // upload complete
                            nUploadedFiles++;
                            // if all files have finished uploading inform uploadFiles
                            if(totalFiles === nUploadedFiles){
                                console.log("finished uploading")
                                resolve();
                            }
                        }
                    );
                });

                this.filesToUpload = []
            })

        },
        getFileExtension(filename) { // can be moved into helpers library if needs to be used elsewhere
            return filename.substring(filename.lastIndexOf('.'), filename.length) || filename;
        }
    },
    computed: {
        orgRef() {
            return this.user.orgId ? organisationsCollection.doc(this.user.orgId) : null;
        }
    }
}
</script>

<style scoped lang="scss">
.content {
    max-width: 400px;
}
.dialog-container {
    margin: 0px 15px 0px 15px;
}
.upload-polygon-button > .md-button{
    background-color: #ffff;
    box-shadow: #0000004d 0px 1px 4px -1px;
    margin: 0;
}
.polygon-upload {
    margin: 6px 6px 0 0;
}
.upload-container {
    max-height: 250px;
}
.progress-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    margin: 0 5px 0 10px;
    .md-progress-bar {
        width: 100%;
    }
}
</style>